import { isNotNullish } from '@adornis/assert/isNullish.js';
import { A } from '@adornis/base/env-info.js';

type CacheResult = Record<string, any>;

const cachingFunctions = new Map<string, () => Promise<Array<CacheResult>>>();
const cache = new Map<string, Promise<Array<CacheResult>>>();

export function registerCache(
  key: string,
  functions: {
    serverCachingFunction: () => Promise<Array<CacheResult>>;
    clientCachingFunction: () => Promise<Array<CacheResult>>;
  },
  options?: {
    retentionTimeMs?: number;
  },
): void {
  if (cache.has(key)) {
    throw new Error(`Cache key ${key} is already registered`);
  }
  // default reention time is 5 minutes
  const retentionTimeMs = options?.retentionTimeMs ?? 300000;

  const cachingFunction = A.isClient ? functions.clientCachingFunction : functions.serverCachingFunction;
  cachingFunctions.set(key, cachingFunction);

  setInterval(() => {
    cache.delete(key);
  }, retentionTimeMs);
}

export function getCache<T extends CacheResult>(key: string): Promise<Array<T>> {
  const result = cache.get(key);
  if (isNotNullish(result)) {
    return result as Promise<Array<T>>;
  }
  const cachingFunction = cachingFunctions.get(key);
  if (cachingFunction) {
    const result = cachingFunction();
    cache.set(key, result);
    return result as Promise<Array<T>>;
  }

  return Promise.resolve([]);
}

export async function repopulateCache<T extends CacheResult>(key: string): Promise<Array<T>> {
  const cachingFunction = cachingFunctions.get(key);
  if (cachingFunction) {
    const result = cachingFunction();
    cache.set(key, result);
    return result as Promise<Array<T>>;
  }

  return [];
}
